import { Subject } from "rxjs";
import { isNullOrUndefined, isStrEmpty } from "@core/helpers/helpers";
import StorageService from "../storage";
import { HttpMethod, HttpOptions, RequestContentType } from "./http.type";
import {
  ACCESS_TOKEN_KEY,
  CONFLICT_STATUS_CODE,
  DEVICE_ID,
  REFRESH_TOKEN_KEY,
  RoutePath,
  TOKEN_EXPIRED_CODE,
} from "@app/constants";
import { Environment } from "@environments/environment";
import { addToast } from "@app/components/toast/toast.service";
import axios, { AxiosResponse } from "axios";
import { clearLocalStorage } from "@app/utils/util";
import store from "@app/store";
import { clearUser } from "@app/store/auth";
import { v4 as uuidv4 } from "uuid";
import router from "@app/router";

class _HttpService {
  public isRequesting$ = new Subject<boolean>();
  public onError$ = new Subject<any>();

  private instance = axios.create();

  constructor() {
    this.instance.interceptors.request.use((config) => {
      const accessToken = this.getAccessToken();

      if (accessToken) {
        config.headers["Authorization"] = `Bearer ${accessToken}`;
      }

      return config;
    });

    this.instance.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const config = error.config;
        const refreshToken = StorageService.get(REFRESH_TOKEN_KEY);

        const status = error?.response?.data?.status;

        if (error?.response?.status === CONFLICT_STATUS_CODE) {
          clearLocalStorage();
          store.dispatch(clearUser());
          router.navigate(RoutePath.HOME);

          return;
        }

        if (status === TOKEN_EXPIRED_CODE) {
          try {
            const res = await axios(
              `${Environment.BASE_API}/user/api/v1/auth/access-token`,
              {
                method: HttpMethod.POST,
                data: {
                  refreshToken,
                },
              }
            );

            StorageService.set(ACCESS_TOKEN_KEY, res.data.data.accessToken);
            StorageService.set(REFRESH_TOKEN_KEY, res.data.data.refreshToken);

            config.headers["Authorization"] =
              `Bearer ${res.data.data.accessToken}`;

            return this.instance(config);
          } catch (err) {
            clearLocalStorage();
            store.dispatch(clearUser());

            return Promise.reject(err);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  public get<T>(uri: string, options?: HttpOptions) {
    return this.request<T>(uri, HttpMethod.GET, options);
  }

  public post<T>(uri: string, options?: HttpOptions) {
    return this.request<T>(uri, HttpMethod.POST, options);
  }

  public patch<T>(uri: string, options?: HttpOptions) {
    return this.request<T>(uri, HttpMethod.PATCH, options);
  }

  public put<T>(uri: string, options?: HttpOptions) {
    return this.request<T>(uri, HttpMethod.PUT, options);
  }

  public delete<T>(uri: string, options?: HttpOptions) {
    return this.request<T>(uri, HttpMethod.DELETE, options);
  }

  public async request<T>(uri: string, method: string, options?: HttpOptions) {
    try {
      let url = this.resolveUri(uri);

      if (options?.queryParams) {
        url = url + "?" + this.generateHttpParams(options?.queryParams);
      }

      let body = this.buildBodyData(options?.body);

      if (options?.requestContentType) {
        switch (options?.requestContentType) {
          case RequestContentType.MULTIPART:
            body = this.buildFormData(options?.body);
            break;
          case RequestContentType.BINARY_STREAM:
            body = options.body;
            break;
        }
      }

      let headers = {};

      if (options?.headers) {
        headers = options.headers;
      }

      headers = {
        ...headers,
        deviceId: this.getDeviceId(),
      };

      const isShowLoading = options?.isShowLoading ?? true;

      if (isShowLoading) {
        this.isRequesting$.next(true);
      }

      const res = await this.instance.request<T, AxiosResponse<T>>({
        url,
        method,
        data: body,
        headers,
        signal: options?.signal,
      });

      return res.data;
    } catch (error: any) {
      this.onError$.next(error);

      if (typeof error?.response?.data?.message === "string") {
        const message = error?.response?.data?.message;

        addToast({ text: message, status: "inValid" });
      }

      throw error;
    } finally {
      this.isRequesting$.next(false);
    }
  }

  public getDeviceId() {
    let deviceId = StorageService.get(DEVICE_ID);

    if (deviceId === null) {
      const randomId = uuidv4();

      deviceId = `${randomId}`;
      StorageService.set(DEVICE_ID, deviceId);
    }

    return deviceId;
  }

  protected buildBodyData(data: any) {
    return data || Object.create(null);
  }

  protected buildFormData(data: any) {
    const formData = new FormData();

    for (const key in data) {
      if (data[key] !== undefined) {
        if (data[key] instanceof File) {
          formData.append(key, data[key], data[key].name);
        } else if (Array.isArray(data[key])) {
          for (const item of data[key]) {
            formData.append(key, item);
          }
        } else if (typeof data[key] === "boolean") {
          formData.append(key, JSON.stringify(data[key]));
        } else {
          formData.append(key, data[key]);
        }
      }
    }

    return formData;
  }

  private resolveUri(uri: string): string {
    if (/^(http|https):\/\/.+$/.test(uri)) {
      return uri;
    }

    return `${Environment.BASE_API}${uri}`;
  }

  private generateHttpParams(params: object) {
    const httpParams: string[] = [];
    const objectToQueryString = (obj: object, prefix?: any) => {
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const k = prefix ? prefix + "[" + key + "]" : key;
          const v = (obj as any)[key];

          if (Array.isArray(v)) {
            for (const vv of v) {
              httpParams.push(k + "=" + vv);
            }
          } else if (v !== null && typeof v === "object") {
            objectToQueryString(v, k);
          } else {
            if (!isNullOrUndefined(v) && !isStrEmpty(v.toString())) {
              httpParams.push(k + "=" + v);
            }
          }
        }
      }
    };

    objectToQueryString(params);

    return encodeURI(httpParams.join("&"));
  }

  public getAccessToken() {
    return (
      StorageService.get(ACCESS_TOKEN_KEY) ||
      StorageService.getSession(ACCESS_TOKEN_KEY)
    );
  }
}

const HttpService = new _HttpService();

export default HttpService;
